/**
 * Simple API Service
 * Handles all HTTP communication with the backend via proxy
 */

const API_BASE_URL = '/api';

/**
 * One session renewal's outcome (audit bug 81): a new key; refused by the server (the renewer has
 * signed the person out); or the server could not be reached (nobody is signed out for that).
 */
export type SessionRenewal = { token: string } | { refused: true } | { unreachable: true };

const SIGNED_OUT_MESSAGE = 'Authentication required. Please login again.';
const UNREACHABLE_MESSAGE = 'The server could not be reached. Try again in a moment.';

export class ApiService {
  private getViewAs: (() => { header: string; siteId?: string | null } | null) | null = null;
  private getAccessToken: (() => string | null) | null = null;
  private onUnauthorized: (() => void) | null = null;
  private sessionRenewer: (() => Promise<SessionRenewal>) | null = null;
  private renewing: Promise<SessionRenewal> | null = null;

  // ---------------------------------------------------------------------------
  // AUTH-READY BARRIER (2026-07-22): boot-time consumers used to race the auth
  // refresh and fire unauthenticated requests (guaranteed 401 on every cold
  // boot — see useUserPreference). There was no "auth established" concept to
  // wait on. This barrier is that concept: AuthContext resolves it on login /
  // successful refresh and re-arms it on logout; EmbedAuthProvider resolves it
  // synchronously (its token is static). Nothing existing awaits it — opt-in
  // only, so it cannot change the behavior of current call sites.
  private authReadyResolve: (() => void) | null = null;
  private authReadyPromise: Promise<void> = new Promise((resolve) => {
    // Executor runs synchronously; authReadyResolve is declared (and
    // initialized) above, so this assignment is safe field-init order.
    this.authReadyResolve = resolve;
  });

  /** Resolves once a usable auth credential is in place. Re-arms on logout. */
  whenAuthReady(): Promise<void> {
    return this.authReadyPromise;
  }

  /** AuthContext/EmbedAuthProvider: signal that authenticated calls may proceed. */
  setAuthReady(): void {
    if (this.authReadyResolve) {
      this.authReadyResolve();
      this.authReadyResolve = null;
    }
  }

  /** AuthContext on logout: re-arm the barrier for the next session. */
  resetAuthReady(): void {
    if (this.authReadyResolve) return; // still armed — nothing to do
    this.authReadyPromise = new Promise((resolve) => {
      this.authReadyResolve = resolve;
    });
  }

  /**
   * Set the function to get the current access token
   * This should be called by AuthContext
   */
  setTokenGetter(getter: () => string | null) {
    this.getAccessToken = getter;
  }

  /**
   * "View as" — the identity an ADMIN is currently looking through. Same shape as the token
   * getter, and for the same reason: the context owns the state, this client only reads it
   * at request time so a switch takes effect immediately.
   *
   * The server honours it on GET only, and only for a real admin (middleware/viewAs.js), so
   * sending it is harmless when it is not deserved.
   */
  setViewAsGetter(getter: () => { header: string; siteId?: string | null } | null) {
    this.getViewAs = getter;
  }

  /**
   * Set the callback for unauthorized responses (401)
   * This should be called by AuthContext to trigger logout
   */
  setUnauthorizedCallback(callback: () => void) {
    this.onUnauthorized = callback;
  }

  /**
   * SESSION EXPIRED ≠ SIGNED OUT (audit bug 81; Bar signed out in the middle of work, 13 Oct 2026).
   * The session key lives 15 minutes and is renewed by a timer; a tab whose timer slept (a closed
   * laptop, a hidden tab) sends its next request with an expired key. That answer (401) used to
   * sign the person out. Now the request renews the session once and is sent again; only a
   * renewal the server refuses signs out. AuthContext registers the renewer; the embed does not,
   * and keeps its own handling.
   */
  setSessionRenewer(renewer: (() => Promise<SessionRenewal>) | null) {
    this.sessionRenewer = renewer;
  }

  /** One renewal at a time, shared by every request (and the timer, and uploads) that needs it. */
  renewSession(): Promise<SessionRenewal> {
    if (!this.sessionRenewer) return Promise.resolve({ refused: true });
    if (!this.renewing) {
      this.renewing = this.sessionRenewer().finally(() => { this.renewing = null; });
    }
    return this.renewing;
  }

  hasSessionRenewer(): boolean {
    return Boolean(this.sessionRenewer);
  }

  /**
   * A 401 on `url`: renew and send again with the new key. Returns the second answer, or throws
   * when the renewal was refused (already signed out) or the server could not be reached. The
   * sign-in requests themselves (login, refresh, logout, forgot/reset password) answer 401 for their
   * own reasons and are not renewed for.
   */
  private async sendAgainAfterRenewal(url: string, send: (token: string) => Promise<Response>): Promise<Response | null> {
    if (!this.sessionRenewer || /^\/api\/auth\/(login|refresh|logout|forgot-password|reset-password)\b/.test(url)) return null;
    const renewal = await this.renewSession();
    if ('token' in renewal) return send(renewal.token);
    if ('unreachable' in renewal) throw new Error(UNREACHABLE_MESSAGE);
    throw new Error(SIGNED_OUT_MESSAGE);
  }

  /**
   * Notify the global unauthorized handler (registered via setUnauthorizedCallback).
   * Use this from services that make direct fetch/XHR calls outside of apiService
   * methods, so their 401s trigger the same logout flow as every other API call.
   */
  notifyUnauthorized(): void {
    if (this.onUnauthorized) {
      this.onUnauthorized();
    }
  }

  /**
   * Get the raw access token for use in URL query parameters (e.g. img src)
   */
  getToken(): string | null {
    return this.getAccessToken ? this.getAccessToken() : null;
  }

  /**
   * Get auth headers for use with direct fetch() calls
   * Use this when you need to make authenticated requests outside of apiService methods
   */
  getAuthHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
    };

    if (this.getAccessToken) {
      const token = this.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
    data?: any
  ): Promise<T> {
    // Tolerate already-prefixed endpoints (config-provided paths) — never double-prefix.
    const url = endpoint.startsWith('/api') ? endpoint : `${API_BASE_URL}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
    };

    // Add Authorization header if we have an access token
    if (this.getAccessToken) {
      const token = this.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // Only on reads. A write always runs as the real person — an admin keeps their own
    // powers while looking at somebody else's screen, which is the agreed behaviour.
    if (this.getViewAs && method === 'GET') {
      const viewAs = this.getViewAs();
      if (viewAs) {
        headers['X-View-As'] = viewAs.header;
        if (viewAs.siteId) headers['X-View-As-Site'] = viewAs.siteId;
      }
    }

    const config: RequestInit = {
      method,
      headers,
      credentials: 'include', // Important: Include cookies for refresh token
      cache: 'no-store',
    };

    if (data && method !== 'GET') {
      config.body = JSON.stringify(data);
    }

    try {
      let response = await fetch(url, config);

      // An expired session: renew once and send again (audit bug 81).
      if (response.status === 401) {
        const again = await this.sendAgainAfterRenewal(url, token => fetch(url, { ...config, headers: { ...headers, Authorization: `Bearer ${token}` } }));
        if (again) response = again;
      }

      // Still 401 (or no renewer: the embed) - as before, the person is signed out.
      if (response.status === 401 && this.onUnauthorized) {
        this.onUnauthorized();
        throw new Error(SIGNED_OUT_MESSAGE);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      // Callers own failure logging/toasts (F-delta: the internal console.error here
      // double-logged every failure alongside the caller's own catch).
      throw error;
    }
  }

  // Health check
  async healthCheck(): Promise<{ status: string; database: string }> {
    return this.request('/health');
  }

  // Assets
  async getAssets(): Promise<any[]> {
    return this.request('/assets');
  }

  async getAsset(id: string): Promise<any> {
    return this.request(`/assets/${id}`);
  }

  async createAsset(data: { name: string; description?: string }): Promise<any> {
    return this.request('/assets', 'POST', data);
  }

  async updateAsset(id: string, data: { name: string; description?: string }): Promise<any> {
    return this.request(`/assets/${id}`, 'PUT', data);
  }

  async patchAsset(id: string, data: Partial<{ name: string; description: string; custom_data: string }>): Promise<any> {
    return this.request(`/assets/${id}`, 'PATCH', data);
  }

  // Generic patch method for any endpoint
  async patch<T>(endpoint: string, data: any): Promise<T> {
    return this.request(endpoint, 'PATCH', data);
  }

  async deleteAsset(id: string): Promise<{ success: boolean; id: string }> {
    return this.request(`/assets/${id}`, 'DELETE');
  }

  // Settings
  async getSetting(key: string): Promise<any> {
    return this.request(`/settings/${key}`);
  }

  async saveSetting(key: string, value: any): Promise<{ success: boolean }> {
    return this.request(`/settings/${key}`, 'POST', { value });
  }

  // File operations live in FileService (upload/delete/set-default with progress tracking).

  // Generic POST method for custom API calls
  async post(endpoint: string, data: any): Promise<any> {
    return this.request(endpoint, 'POST', data);
  }

  // Generic GET method for custom API calls
  async get(endpoint: string): Promise<any> {
    return this.request(endpoint, 'GET');
  }

  // Generic PUT method for custom API calls
  async put(endpoint: string, data: any): Promise<any> {
    return this.request(endpoint, 'PUT', data);
  }

  // Generic DELETE method for custom API calls (optional JSON body, e.g. batch-delete)
  async delete(endpoint: string, data?: any): Promise<any> {
    return this.request(endpoint, 'DELETE', data);
  }

  /**
   * Multipart upload (FormData). No explicit Content-Type — the browser sets the
   * multipart boundary. Auth + 401 handling identical to request().
   */
  async upload(endpoint: string, formData: FormData, method: 'POST' | 'PUT' = 'POST'): Promise<any> {
    const response = await this.rawFetch(endpoint, { method, body: formData });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Authenticated fetch escape hatch for non-JSON responses (blobs, images, streamed
   * downloads) and callers that must inspect the Response themselves. Applies the same
   * auth header, credentials, and global-401 handling as every other method — NO caller
   * anywhere in src/ should use bare fetch() against /api.
   */
  async rawFetch(endpoint: string, init: RequestInit = {}): Promise<Response> {
    // Absolute/blob/data URLs pass through; bare endpoints get the /api prefix.
    const isAbsolute = /^(https?:|blob:|data:)/.test(endpoint) || endpoint.startsWith('/api');
    const url = isAbsolute ? endpoint : `${API_BASE_URL}${endpoint}`;
    const headers: Record<string, string> = {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      ...(init.headers as Record<string, string> | undefined),
    };
    const token = this.getAccessToken ? this.getAccessToken() : null;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response = await fetch(url, {
      credentials: 'include',
      cache: 'no-store',
      ...init,
      headers,
    });

    if (response.status === 401) {
      const again = await this.sendAgainAfterRenewal(url, token => fetch(url, {
        credentials: 'include',
        cache: 'no-store',
        ...init,
        headers: { ...headers, Authorization: `Bearer ${token}` },
      }));
      if (again) response = again;
    }

    if (response.status === 401 && this.onUnauthorized) {
      this.onUnauthorized();
    }
    return response;
  }

  /** Authenticated blob download (e.g. images, file exports). Throws on non-ok. */
  async getBlob(endpoint: string): Promise<Blob> {
    const response = await this.rawFetch(endpoint);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.blob();
  }
}

// Singleton instance
export const apiService = new ApiService();
